/**
 * portalApi — pure axios functions for the admin portal endpoints.
 * State, toasts and navigation live in the usePortal hooks, not here.
 */

import portalClient from "./portalClient";
import { API_ENDPOINTS } from "../constants/api";
import { LoginFormType } from "../types/login";
import {
  DashboardCreatePayload,
  DashboardTiersAssignPayload,
  DashboardUpdatePayload,
  PortalDashboard,
  PortalLoginResponse,
  PortalMe,
  PortalProduct,
  PortalProgram,
  PortalProgramCreated,
  PortalRoleEntry,
  PortalTier,
  PortalTierRef,
  PortalUser,
  PortalWidgetTemplate,
  ProductCreatePayload,
  ProductUpdatePayload,
  ProgramCreatePayload,
  ProgramUpdatePayload,
  TierCreatePayload,
  TierUpdatePayload,
  UserCreatePayload,
  UserProfileUpdatePayload,
  WidgetTemplateCreatePayload,
  WidgetTemplateUpdatePayload,
  WidgetTemplateUpdateResponse,
} from "../types/portal";

const P = API_ENDPOINTS.PORTAL;
const withId = (path: string, id: number) => path.replace(":id", String(id));

/** POST /admin/auth/login */
export const portalLogin = async (
  credentials: LoginFormType,
): Promise<PortalLoginResponse> =>
  (await portalClient.post<PortalLoginResponse>(P.LOGIN, credentials)).data;

/** GET /admin/auth/me */
export const getPortalMe = async (): Promise<PortalMe> =>
  (await portalClient.get<PortalMe>(P.ME)).data;

/** GET /admin/users */
export const listPortalUsers = async (): Promise<PortalUser[]> =>
  (await portalClient.get<PortalUser[]>(P.USERS)).data;

/** GET /admin/roles */
export const listPortalRoles = async (): Promise<PortalRoleEntry[]> =>
  (await portalClient.get<PortalRoleEntry[]>(P.ROLES)).data;

/** POST /admin/users */
export const createUser = async (body: UserCreatePayload): Promise<PortalUser> =>
  (await portalClient.post<PortalUser>(P.USERS, body)).data;

/** GET /admin/users/:id — used to refresh one row after a mutation whose own
 *  response is partial (role change, activate) instead of refetching the
 *  whole list. */
export const getUser = async (id: number): Promise<PortalUser> =>
  (await portalClient.get<PortalUser>(withId(P.USER, id))).data;

/** PATCH /admin/users/:id — profile fields */
export const updateUserProfile = async (
  id: number,
  body: UserProfileUpdatePayload,
): Promise<PortalUser> =>
  (await portalClient.patch<PortalUser>(withId(P.USER, id), body)).data;

/** PATCH /admin/users/:id/role */
export const updateUserRole = async (
  id: number,
  role_id: number,
): Promise<PortalUser> =>
  (await portalClient.patch<PortalUser>(withId(P.USER_ROLE, id), { role_id })).data;

/** PATCH /admin/users/:id/programs — full replace of a manager's products.
 *  Path still says "programs" on the wire; the field is product_ids. */
export const updateUserProducts = async (
  id: number,
  product_ids: number[],
): Promise<PortalUser> =>
  (
    await portalClient.patch<PortalUser>(withId(P.USER_PRODUCTS, id), {
      product_ids,
    })
  ).data;

/** PATCH /admin/users/:id/program — move to a different company (super_admin only) */
export const moveUserProgram = async (
  id: number,
  program_id: number,
): Promise<PortalUser> =>
  (
    await portalClient.patch<PortalUser>(withId(P.USER_PROGRAM, id), {
      program_id,
    })
  ).data;

/** PATCH /admin/users/:id/password */
export const updateUserPassword = async (
  id: number,
  password: string,
): Promise<{ id: number; password_updated: boolean }> =>
  (
    await portalClient.patch<{ id: number; password_updated: boolean }>(
      withId(P.USER_PASSWORD, id),
      { password },
    )
  ).data;

/** DELETE /admin/users/:id — soft delete (sets active=false, row stays) */
export const deactivateUser = async (id: number): Promise<void> => {
  await portalClient.delete(withId(P.USER, id));
};

/** PATCH /admin/users/:id/activate */
export const activateUser = async (id: number): Promise<PortalUser> =>
  (await portalClient.patch<PortalUser>(withId(P.USER_ACTIVATE, id))).data;

/** GET /admin/clients — programs (companies) */
export const listPrograms = async (): Promise<PortalProgram[]> =>
  (await portalClient.get<PortalProgram[]>(P.PROGRAMS)).data;

/** POST /admin/clients */
export const createProgram = async (
  body: ProgramCreatePayload,
): Promise<PortalProgramCreated> =>
  (await portalClient.post<PortalProgramCreated>(P.PROGRAMS, body)).data;

/** PATCH /admin/clients/:id */
export const updateProgram = async (
  id: number,
  body: ProgramUpdatePayload,
): Promise<PortalProgram> =>
  (await portalClient.patch<PortalProgram>(withId(P.PROGRAM, id), body)).data;

/** GET /admin/clients/:id/analytics-tiers */
export const getProgramTiers = async (id: number): Promise<PortalTierRef[]> =>
  (await portalClient.get<PortalTierRef[]>(withId(P.PROGRAM_TIERS, id))).data;

/** PATCH /admin/clients/:id/analytics-tiers — full replace */
export const setProgramTiers = async (
  id: number,
  tier_ids: number[],
): Promise<PortalTierRef[]> =>
  (
    await portalClient.patch<PortalTierRef[]>(withId(P.PROGRAM_TIERS, id), {
      tier_ids,
    })
  ).data;

/** GET /admin/programs — products (product lines) */
export const listProducts = async (): Promise<PortalProduct[]> =>
  (await portalClient.get<PortalProduct[]>(P.PRODUCTS)).data;

/** POST /admin/programs */
export const createProduct = async (
  body: ProductCreatePayload,
): Promise<PortalProduct> =>
  (await portalClient.post<PortalProduct>(P.PRODUCTS, body)).data;

/** PATCH /admin/programs/:id */
export const updateProduct = async (
  id: number,
  body: ProductUpdatePayload,
): Promise<PortalProduct> =>
  (await portalClient.patch<PortalProduct>(withId(P.PRODUCT, id), body)).data;

/** GET /admin/analytics-tiers */
export const listTiers = async (): Promise<PortalTier[]> =>
  (await portalClient.get<PortalTier[]>(P.TIERS)).data;

/** POST /admin/analytics-tiers — tiers have no delete endpoint */
export const createTier = async (body: TierCreatePayload): Promise<PortalTier> =>
  (await portalClient.post<PortalTier>(P.TIERS, body)).data;

/** PATCH /admin/analytics-tiers/:id */
export const updateTier = async (
  id: number,
  body: TierUpdatePayload,
): Promise<PortalTier> =>
  (await portalClient.patch<PortalTier>(withId(P.TIER, id), body)).data;

/** GET /admin/dashboards?include_inactive=true — the template catalog. Always
 *  requesting inactive rows too: a deactivated template's tier links survive
 *  the soft delete, so this is also how a super_admin finds and reactivates
 *  one (there's no other list surface for it). */
export const listDashboards = async (): Promise<PortalDashboard[]> =>
  (
    await portalClient.get<PortalDashboard[]>(P.DASHBOARDS, {
      params: { include_inactive: true },
    })
  ).data;

/** POST /admin/dashboards — mutation response is partial and keyed
 *  differently (`dashboard_id`, not `id`); callers already have the code
 *  they sent, so the body isn't consumed here — refetch the list instead. */
export const createDashboard = async (
  body: DashboardCreatePayload,
): Promise<void> => {
  await portalClient.post(P.DASHBOARDS, body);
};

/** PATCH /admin/dashboards/:id */
export const updateDashboard = async (
  id: number,
  body: DashboardUpdatePayload,
): Promise<void> => {
  await portalClient.patch(withId(P.DASHBOARD, id), body);
};

/** PATCH /admin/dashboards/:id/tiers — full replace */
export const setDashboardTiers = async (
  id: number,
  body: DashboardTiersAssignPayload,
): Promise<void> => {
  await portalClient.patch(withId(P.DASHBOARD_TIERS, id), body);
};

/** DELETE /admin/dashboards/:id — soft: is_active=false, tiers/widgets kept */
export const deactivateDashboard = async (id: number): Promise<void> => {
  await portalClient.delete(withId(P.DASHBOARD, id));
};

/** GET /widget_templates — confirmed live: the response is now grouped
 *  server-side by tier (`tiers: [{tier_id, tier_name, widgets}]`, tier_id
 *  null for the "Untiered" bucket) instead of the old dashboard-category
 *  wrapper, and each widget no longer embeds its own `tier` — it's implied
 *  by which bucket it's in. Denormalized back onto each widget here (via
 *  `licensed_tiers` for the code lookup) so the rest of the portal code can
 *  keep reading `widget.tier` same as before. Also now includes inactive
 *  widgets by default with a real `is_active` per row (previously excluded
 *  entirely with no such field at all). */
export const listWidgetTemplates = async (): Promise<PortalWidgetTemplate[]> => {
  const { data } = await portalClient.get<{
    tiers: {
      tier_id: number | null;
      tier_name: string;
      widgets: Omit<PortalWidgetTemplate, "tier">[];
    }[];
    licensed_tiers: PortalTierRef[];
  }>(P.WIDGET_TEMPLATES);

  const codeById = new Map(
    data.licensed_tiers.map((tier) => [tier.id, tier.code]),
  );
  const byId = new Map<number, PortalWidgetTemplate>();
  data.tiers.forEach((bucket) => {
    const tier: PortalTierRef | null =
      bucket.tier_id === null
        ? null
        : {
            id: bucket.tier_id,
            code: codeById.get(bucket.tier_id) ?? bucket.tier_name.toLowerCase(),
            name: bucket.tier_name,
          };
    bucket.widgets.forEach((widget) => byId.set(widget.id, { ...widget, tier }));
  });
  return Array.from(byId.values());
};

/** PATCH /admin/widget-templates/:id */
export const updateWidgetTemplate = async (
  id: number,
  body: WidgetTemplateUpdatePayload,
): Promise<WidgetTemplateUpdateResponse> =>
  (
    await portalClient.patch<WidgetTemplateUpdateResponse>(
      withId(P.WIDGET_TEMPLATE, id),
      body,
    )
  ).data;

/** POST /admin/widget-templates — 409 on duplicate code, 400 for an unknown
 *  or inactive tier_id (both plain-string `detail`, confirmed live). */
export const createWidgetTemplate = async (
  body: WidgetTemplateCreatePayload,
): Promise<PortalWidgetTemplate> =>
  (
    await portalClient.post<PortalWidgetTemplate>(
      P.WIDGET_TEMPLATE_CREATE,
      body,
    )
  ).data;
